import React, { useEffect, useState, useContext, useMemo } from 'react';

import { DateTime } from 'luxon';
import TimeDuration from '../TimeDuration/index';
import { Stack } from '@mui/system';
import { Box } from '@mui/material';
import { Link } from 'react-router-dom';
import '../fonts.css';
import HealthBar from '../HealthBar/HealthBar';
import PostActions from '../postComponent/PostActions';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import { UserRecord, FeedData } from '../model/types';
import { replaceTagsWithLinks } from '../lib';
import FeedIcon from '../resource/FeedIcon';
import AvatarIcon from '../resource/AvatarIcon';
import TombstoneCustomIcon from '../resource/TombstoneCustom';

function hashColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = ((hash << 5) - hash) + id.charCodeAt(i);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 42%)`;
}

function Follow({
  follow,
  expandToReply,
  useParentID,
}: {
  follow: any;
  expandToReply?: string;
  useParentID: boolean;
}) {
  const getUserRecord =
    useContext<CacheContextType>(CacheContext).getUserRecord;
  const getFeedFromID =
    useContext<CacheContextType>(CacheContext).getFeedFromID;
  const [followDisplayRecord, setFollowDisplayRecord] = useState<
    UserRecord | FeedData | null
  >(null);
  const [isExpired, setIsExpired] = useState(false);
  const [displayName, setDisplayName] = useState<string>('');
  const [displayType, setDisplayType] = useState<string>('');
  const [expiryDate, setExpiryDate] = useState<Date>(
    DateTime.fromISO(follow.expire_at ?? '').toJSDate(),
  );
  const [createdTime, setCreatedTime] = useState<DateTime>(
    DateTime.fromISO(follow.created_at ?? ''),
  );
  useEffect(() => {
    setIsExpired(expiryDate < new Date() && !follow?.is_permanent);
    setExpiryDate(DateTime.fromISO(follow.expire_at ?? '').toJSDate());
    setCreatedTime(DateTime.fromISO(follow.created_at ?? ''));

    if (!follow.parent_id || follow.parent_id === undefined) return;
    const fetchID = useParentID ? follow.parent_id : follow.user_record_id;
    if (
      follow.parent_type === undefined ||
      follow.parent_type === 'users' ||
      !useParentID
    ) {
      setDisplayType('users');
      const userRecord = getUserRecord(fetchID);
      setFollowDisplayRecord(userRecord || null);
      setDisplayName(userRecord?.username || '');
    } else if (follow.parent_type === 'feeds' && useParentID) {
      setDisplayType('feeds');
      const feedRecord = getFeedFromID(fetchID);
      setFollowDisplayRecord(feedRecord || null);
      setDisplayName(feedRecord?.feed_tag || '');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [follow]);
  const { accentColor, timePct } = useMemo(() => {
    if (follow?.is_permanent) return { accentColor: 'gold', timePct: 100 };
    if (isExpired) return { accentColor: '#616161', timePct: 0 };
    const created = DateTime.fromISO(follow.created_at ?? '').toSeconds();
    const expire = DateTime.fromISO(follow.expire_at ?? '').toSeconds();
    const total = expire - created;
    if (total <= 0) return { accentColor: '#b51e1b', timePct: 0 };
    const left = expire - DateTime.now().toSeconds();
    const pct = Math.max(0, Math.min(100, (left / total) * 100));
    const t = pct / 100;
    const r = Math.round(181 * (1 - t) + 22 * t);
    const g = Math.round(30 * (1 - t) + 168 * t);
    return { accentColor: `rgb(${r},${g},27)`, timePct: pct };
  }, [follow.expire_at, follow.is_permanent, isExpired]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        bgcolor: 'background.paper',
        margin: '0 auto',
        borderRadius: '6px',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: `3px solid ${accentColor}`,
        padding: 1.5,
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s ease',
        '&:hover': {
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        },
        ...(!isExpired && !follow?.is_permanent && (() => {
          const left = DateTime.fromISO(follow.expire_at ?? '').toSeconds() - DateTime.now().toSeconds();
          return left > 0 && left < 3600;
        })() && {
          animation: 'borderPulse 1.5s ease-in-out infinite',
          '@keyframes borderPulse': {
            '0%, 100%': { borderLeftColor: '#b51e1b' },
            '50%': { borderLeftColor: 'rgba(181,30,27,0.2)' },
          },
        }),
      }}
      id={follow.id}
    >
      <Stack direction="row" spacing={1}>
        <HealthBar objectRecord={follow}>
          {displayType === 'feeds' ? (
              <FeedIcon />
            ) : (
              <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: hashColor(follow.user_record_id ?? ''), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AvatarIcon style={{ width: 24, height: 24, opacity: 0.9 }} />
              </Box>
            )}
        </HealthBar>
        <Stack direction="column">
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
            <Box sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary' }}>
              {!followDisplayRecord ? 'loading' : (
                <Link to={'/' + displayName} style={{ color: 'inherit', textDecoration: 'none' }}>
                  {displayName}
                </Link>
              )}
            </Box>
            <Box sx={{ color: 'text.secondary', fontSize: '13px' }}>
              {displayType === 'users' ? '@' : '#'}{displayName}
            </Box>
            <Box sx={{ color: 'text.disabled', fontSize: '13px' }}>·</Box>
            <Box sx={{ color: 'text.secondary', fontSize: '13px' }}>
              <Link to={`/posts/${follow.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                <TimeDuration jsDate={createdTime.toJSDate()} invertSign={true} titleDisplay="absoluteTime" /> ago
              </Link>
            </Box>
            <Box sx={{ color: 'text.disabled', fontSize: '13px' }}>·</Box>
            <Box sx={{ fontSize: '13px', color: isExpired ? 'error.main' : follow?.is_permanent ? 'warning.main' : 'text.secondary' }}>
              {isExpired ? (
                <>expired <TimeDuration jsDate={expiryDate} /> ago</>
              ) : follow?.is_permanent ? (
                'IMMORTAL'
              ) : (
                <><TimeDuration jsDate={expiryDate} /> left</>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              color: isExpired ? 'text.disabled' : 'text.primary',
              fontSize: '15px',
              fontWeight: 400,
              fontStyle: isExpired ? 'italic' : 'normal',
              lineHeight: 1.6,
              wordBreak: 'break-word',
            }}
            justifyContent="center"
          >
            {isExpired ? (
              <Stack direction="column" spacing={0.5}>
                <Box
                  sx={{ width: '50%', height: '50%' }}
                  justifyContent="center"
                >
                  <TombstoneCustomIcon
                    className="font-face-nokia"
                    username={displayName || 'unknown'}
                    from={DateTime.fromISO(
                      follow?.created_at ?? '',
                    ).toFormat("EEEE',' MMMM d',' ha")}
                    to={DateTime.fromISO(follow?.expire_at ?? '').toFormat(
                      "EEEE',' MMMM d',' ha",
                    )}
                    objectType="follow"
                    style={{ width: '200px', height: '250px' }}
                  />
                </Box>
                <Box>this follow is expired, vote to revive it</Box>
              </Stack>
            ) : (
              replaceTagsWithLinks(follow.value)
            )}
          </Box>
          <PostActions
            post={follow}
            expandToReply={expandToReply}
            objectType="follows"
            parentType="follows"
          />
        </Stack>
      </Stack>

      {/* Time-remaining bar */}
      {!isExpired && !follow?.is_permanent && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: '2px',
            width: `${timePct}%`,
            bgcolor: accentColor,
            transition: 'width 60s linear, background-color 30s ease',
            borderRadius: '0 1px 0 0',
          }}
        />
      )}
    </Box>
  );
}

export default Follow;
